<script>
import MeetingStatusBadge from '../channel/meeting-status-badge.vue'

export default {
  name: "UserTimeline",
  components: { MeetingStatusBadge },
  data() {
    return {
      items: [],
      loading: false,
      selectedYear: null, // null -> upcoming mode
      yearOptions: []     // built from current year back a few years
    };
  },
  created() {
    // Build a reasonable year filter list (current year - 6)
    const now = new Date();
    const currentYear = now.getFullYear();
    const years = [];
    for (let y = currentYear; y >= currentYear - 6; y--) {
      years.push(y);
    }
    this.yearOptions = years;

    this.fetchData();
  },

  methods: {
    avatarUrl(item) {
      const name = encodeURIComponent(item.name || item.email || 'User')
      let url = `https://ui-avatars.com/api/?name=${name}&size=128&background=0D8ABC&color=fff`
      return item.avatar_url || url
    },
    // Fetch timeline data via axios using an injected $api instance
    async fetchData() {
      this.loading = true;
      try {
        const params = {};
        if (this.selectedYear) params.year = this.selectedYear;

        // Use your unified DRF endpoint
        const { data } = await this.$api.get("/user-timeline/", { params });

        // Normalize incoming items to ensure stable shape for the component.
        // Also tolerate Meeting payloads that might still use `summary` instead of `title`.
        this.items = (data.items || []).map((it) => {
          const title = it.title || it.summary || ""; // fallback to summary if needed
          return {
            type: it.type,
            id: it.id,
            start: it.start,
            end: it.end || null,
            title,
            description: it.description || "",
            status: it.status || "",
            avatar_url: it.avatar_url || "",
            location: it.location || "",
            location_spec: it.location_spec || "",
            language: it.language || "",
            name: it.name || ""
          };
        });
      } catch (err) {
        // You can hook this into a global toast/alert system
        // eslint-disable-next-line no-console
        console.error("Failed to load timeline:", err);
        this.items = [];
      } finally {
        this.loading = false;
      }
    },

    // Badge style/label based on item type
    badgeClass(type) {
      const classes = {
        meeting: "bg-light",
        speaker_session: "bg-primary",
        chair_session: "bg-warning text-dark",
        panel_session: "bg-info text-dark",
      };
      return classes[type] || "bg-success";
    },
    badgeLabel(type) {
      const labels = {
        meeting: "Meeting",
        speaker_session: "Speaker",
        chair_session: "Chair",
        panel_session: "Panel",
      };
      return labels[type] || "Session";
    },

    // Key helper
    itemKey(item) {
      return `${item.type}-${item.id}-${item.start}`;
    },

    // Format date/time in a compact German style
    formatDateTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleString("de-DE", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    isNewDate: (function () {
    // closure keeps track of the last date string
    let lastDate = null;
    return function (iso) {
        if (!iso) return null;

        // normalize to YYYY-MM-DD
        const d = new Date(iso);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

        if (dateStr !== lastDate) {
        lastDate = dateStr; // update stored date
        return dateStr;     // new date detected
        }
        return null; // same date as before
    };
    })(),

    formatDate(iso) {
        if (!iso) return "";
        const d = new Date(iso);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    },
    formatTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    },

    // Simple HTML-safe truncation for descriptions
    truncate(html, maxLen) {
      if (!html) return "";
      const text = this.stripHtml(html);
      return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
    },
    stripHtml(html) {
      const div = document.createElement("div");
      div.innerHTML = html;
      return (div.textContent || div.innerText || "").trim();
    }
  }
};
</script>

<template>
  <div class="user-timeline container py-3">
    <!-- Header / Controls -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h5 class="mb-0">Timeline</h5>

      <div class="d-flex align-items-center gap-2">
        <!-- Year filter -->
        <div class="input-group" style="width: 220px;">
          <label class="input-group-text" for="yearSelect">Jahr</label>
          <select id="yearSelect" class="form-select" v-model="selectedYear" @change="fetchData">
            <option :value="null">Bevorstehend</option>
            <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Timeline -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status" aria-hidden="true"></div>
      <div class="mt-2">Lade Timeline …</div>
    </div>

    <div v-else>
      <div v-if="items.length === 0" class="alert alert-secondary">
        Keine Einträge gefunden.
      </div>

      <ul class="timeline list-unstyled position-relative">
        <li v-for="item in items" :key="itemKey(item)" class="timeline-item">

        <!-- Timeline spine dot with time -->
        <div v-if="isNewDate(item.start)" class="badge bg-secondary text-black mb-3">
            <small class="fw-bold">{{ formatDate(item.start) }}</small>
        </div>

        <div class="d-flex">

        <!-- Timeline spine dot with time -->
        <div class="timeline-dot flex-shrink-0 d-flex align-items-center justify-content-center text-white" :class="badgeClass(item.type)">
            <small class="fw-bold">{{ formatTime(item.start) }}</small>
        </div>


          <!-- Card -->
          <div class="card shadow-sm flex-grow-1 ms-3">
            <div class="card-body d-flex">
              <!-- Avatar -->
              <img
                :src="avatarUrl(item)"
                alt="Avatar"
                class="rounded-circle me-3"
                style="width:48px;height:48px;object-fit:cover;"
              />
              <div class="w-100">

                <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
                    <!-- linke Seite: Badge + Title -->
                    <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                        <div class="d-flex mb-2 mb-md-0 me-md-2">
                          <span class="badge me-1" :class="badgeClass(item.type)">
                            {{ badgeLabel(item.type) }}
                          </span>
                          <meeting-status-badge v-if="item.status" :status="item.status" />
                        </div>
                        <h6 class="mb-0">
                        <template v-if="item.type === 'session' && item.detail_url">
                            <a :href="item.detail_url">{{ item.title }}</a>
                        </template>
                        <template v-else>
                            {{ item.title }}
                        </template>
                        </h6>
                    </div>

                    <!-- rechte Seite: Uhrzeit -->
                    <small class="text-muted mt-2 mt-md-0">
                        {{ formatDateTime(item.start) }} <span v-if="item.end">– {{ formatTime(item.end) }}</span>
                    </small>
                </div>

                <!-- Subtitle / meta -->
                <div class="text-muted small mt-1">
                  <span v-if="item.name">{{ item.name }}</span>
                  <span v-if="item.location"> · {{ item.location }}</span>
                  <span v-if="item.language"> · {{ item.language }}</span>
                </div>
                <!-- Bühnenausstattung — nur für Speaker / Chair / Panel -->
                <div v-if="item.location_spec && ['speaker_session', 'chair_session', 'panel_session'].includes(item.type)"
                     class="small text-secondary mt-1">
                  <strong>Bühnenausstattung:</strong> {{ item.location_spec }}
                </div>

                <!-- Description -->
                <p v-if="item.description" class="mb-0 mt-2 text-secondary" v-html="truncate(item.description, 280)"></p>
              </div>
            </div>
          </div>
          </div>
        </li>
      </ul>
    </div>

  </div>
</template>

<style scoped>
/* Vertical timeline styles (Bootstrap-friendly) */

/* Spine */
.timeline::before {
  content: "";
  position: absolute;
  left: 12px; /* align with dots */
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(0, 0, 0, 0.1);
}

/* Each item aligns to the spine */
.timeline-item {
  position: relative;
  margin-bottom: 1.25rem;
}

/* Dot on the spine */
.timeline-dot {
  min-width: 35px;
  height: 35px;
  border-radius: 50%;
  margin-top: 0.5rem;
  margin-left: -.3rem;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px rgba(13,110,253,.25);
  font-size: 0.75rem; /* smaller font to fit */
  text-align: center;
}

.timeline-day-divider {
  display: flex;
  align-items: center;
  margin: 2rem 0 1rem;
  position: relative;
}

.timeline-day-divider .divider-line {
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
}

.timeline-day-divider .divider-label {
  font-size: 0.85rem;
  font-weight: 500;
  margin: 0 1rem;
  white-space: nowrap;
}

/* Card visual polish */
.card {
  border-radius: 1rem;
}

</style>
